/**
 * AudioVariantsTabContent — Full-width audio variant list for the SoundExpert node.
 *
 * Each row keeps playback controls isolated from selection:
 * - Play/pause and waveform clicks preview that variant only
 * - Clicking the rest of the row selects it as the node's active output
 * - Only one variant preview can play at a time
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWaveform } from './AudioWaveformShared'
import { sortAudioVariantsNewestFirst } from '../SoundExpertNode/soundExpertUtils'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AudioVariant {
  index: number
  audioUrl: string | null
  prompt: string
  mode: string
  durationSeconds?: number
}

export interface AudioVariantsTabContentProps {
  variants: AudioVariant[]
  selectedIndex: number
  batchPending: number
  onSelect: (index: number) => void
}

// ─── Constants ──────────────────────────────────────────────────────────────

const ACCENT = {
  base: '#06b6d4',
  bg20: 'rgba(6, 182, 212, 0.2)',
  bg40: 'rgba(6, 182, 212, 0.4)',
}

const MODE_LABEL_KEYS: Record<string, string> = {
  music: 'audioVariantsTabContent.modeMusic',
  speech: 'audioVariantsTabContent.modeSpeech',
  sfx: 'audioVariantsTabContent.modeSfx',
}

const WAVEFORM_BAR_COUNT = 200
const WAVEFORM_HEIGHT = 44

/** Format seconds as mm:ss */
export function formatMMSS(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '00:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

// ─── Variant Row ────────────────────────────────────────────────────────────

interface VariantRowProps {
  variant: AudioVariant
  isSelected: boolean
  onSelect: () => void
  onStartPlayback: (index: number, element: HTMLAudioElement) => void
  onPlaybackStopped: (index: number) => void
}

const VariantRow = memo(function VariantRow({
  variant,
  isSelected,
  onSelect,
  onStartPlayback,
  onPlaybackStopped,
}: VariantRowProps) {
  const { t } = useTranslation('canvas')
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(variant.durationSeconds ?? 0)
  const bars = useWaveform(variant.audioUrl, WAVEFORM_BAR_COUNT)

  useEffect(() => {
    setCurrentTime(0)
    setIsPlaying(false)
    setDuration(variant.durationSeconds ?? 0)
  }, [variant.audioUrl, variant.durationSeconds])

  const handleTogglePlay = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      const el = audioRef.current
      if (!el || !variant.audioUrl) return

      if (el.paused) {
        onStartPlayback(variant.index, el)
        void el.play()
      } else {
        el.pause()
      }
    },
    [onStartPlayback, variant.audioUrl, variant.index],
  )

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      const el = audioRef.current
      const effectiveDuration = duration || el?.duration || 0
      if (!el || !variant.audioUrl || !effectiveDuration) return

      const rect = e.currentTarget.getBoundingClientRect()
      const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
      el.currentTime = fraction * effectiveDuration
      setCurrentTime(el.currentTime)
    },
    [duration, variant.audioUrl],
  )

  const progress = duration > 0 ? currentTime / duration : 0
  const playedBars = Math.floor(progress * WAVEFORM_BAR_COUNT)
  const modeLabelKey = MODE_LABEL_KEYS[variant.mode]
  const modeLabel = modeLabelKey ? t(modeLabelKey) : variant.mode
  const isUnavailable = !variant.audioUrl

  return (
    <div
      onClick={isUnavailable ? undefined : onSelect}
      className={`rounded-xl border px-3 py-3 transition-colors ${
        isSelected
          ? 'border-primary/60 bg-primary/10'
          : 'border-border-subtle bg-surface-0 hover:bg-surface-1'
      } ${isUnavailable ? 'opacity-70 cursor-default' : 'cursor-pointer'}`}
      data-testid={`variant-row-${variant.index}`}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={handleTogglePlay}
          disabled={isUnavailable}
          className="mt-0.5 h-8 w-8 rounded-full border border-input-border bg-surface-1 text-foreground flex items-center justify-center transition-colors hover:bg-surface-3 hover:border-input-border-hover disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`variant-play-${variant.index}`}
          aria-label={isPlaying ? t('audioVariantsTabContent.pauseVariant', { number: variant.index + 1 }) : t('audioVariantsTabContent.playVariant', { number: variant.index + 1 })}
        >
          {isPlaying ? (
            <svg className="w-3 h-3" fill={ACCENT.base} viewBox="0 0 24 24">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <svg className="w-3 h-3 ml-0.5" fill={ACCENT.base} viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs font-medium text-foreground">{t('audioVariantsTabContent.versionLabel', { number: variant.index + 1 })}</span>
              <span
                className="rounded-full px-1.5 py-0.5 text-xxs border border-border-subtle bg-surface-1 text-muted-foreground"
                data-testid={`variant-mode-badge-${variant.index}`}
              >
                {modeLabel}
              </span>
              {isSelected && (
                <span className="rounded-full px-1.5 py-0.5 text-xxs bg-primary/15 text-primary">
                  {t('audioVariantsTabContent.selected')}
                </span>
              )}
            </div>
            {variant.prompt ? (
              <span className="max-w-[140px] truncate text-xxs text-text-muted" title={variant.prompt}>
                {variant.prompt}
              </span>
            ) : null}
          </div>

          <div
            className={`relative mt-2 ${isUnavailable ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            style={{ height: WAVEFORM_HEIGHT }}
            onClick={isUnavailable ? undefined : handleSeek}
            data-testid={`variant-waveform-${variant.index}`}
          >
            <div className="absolute inset-0 flex items-center gap-[0.5px]">
              {bars.length > 0
                ? bars.map((amp, i) => {
                    const barHeight = Math.max(8, amp * 100)
                    return (
                      <div
                        key={i}
                        className="flex-1 rounded-[0.5px]"
                        style={{
                          height: `${barHeight}%`,
                          background: ACCENT.base,
                          opacity: i < playedBars ? 0.8 : 0.35,
                          transition: i === playedBars ? 'opacity 100ms' : undefined,
                        }}
                      />
                    )
                  })
                : Array.from({ length: WAVEFORM_BAR_COUNT }, (_, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-[0.5px]"
                      style={{
                        height: '8%',
                        background: ACCENT.base,
                        opacity: 0.2,
                      }}
                    />
                  ))}
            </div>

            {!isUnavailable && progress > 0 && (
              <div
                className="absolute top-0 bottom-0 w-[2px] pointer-events-none"
                style={{ left: `${progress * 100}%`, background: ACCENT.base }}
                data-testid={`variant-waveform-cursor-${variant.index}`}
              />
            )}
          </div>

          <div className="mt-1 flex items-center justify-between gap-2 text-xxs text-muted-foreground">
            <span className="font-mono tabular-nums">
              {isUnavailable
                ? t('audioVariantsTabContent.audioUnavailable')
                : `${formatMMSS(currentTime)} / ${formatMMSS(duration)}`}
            </span>
          </div>
        </div>
      </div>

      {variant.audioUrl && (
        <audio
          ref={audioRef}
          src={variant.audioUrl}
          preload="metadata"
          onLoadedMetadata={() => {
            const el = audioRef.current
            if (el && isFinite(el.duration) && el.duration > 0) {
              setDuration(el.duration)
            }
          }}
          onPlay={() => {
            setIsPlaying(true)
            onStartPlayback(variant.index, audioRef.current!)
          }}
          onPause={() => {
            setIsPlaying(false)
            onPlaybackStopped(variant.index)
          }}
          onEnded={() => {
            setIsPlaying(false)
            setCurrentTime(0)
            onPlaybackStopped(variant.index)
          }}
          onTimeUpdate={() => {
            const el = audioRef.current
            if (!el) return
            setCurrentTime(el.currentTime)
            if ((duration === 0 || !isFinite(duration)) && isFinite(el.duration) && el.duration > 0) {
              setDuration(el.duration)
            }
          }}
        />
      )}
    </div>
  )
})

VariantRow.displayName = 'VariantRow'

// ─── Pending Row ────────────────────────────────────────────────────────────

const PendingRow = memo(function PendingRow({ index }: { index: number }) {
  const { t } = useTranslation('canvas')
  return (
    <div
      className="rounded-xl border border-border-subtle bg-surface-0 px-3 py-3 flex items-center gap-2.5"
      data-testid={`variant-pending-${index}`}
    >
      <div
        className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin flex-shrink-0"
        style={{ borderColor: ACCENT.base, borderTopColor: 'transparent' }}
      />
      <span className="text-xxs text-text-muted">{t('audioVariantsTabContent.generating')}</span>
    </div>
  )
})

PendingRow.displayName = 'PendingRow'

// ─── Main Component ─────────────────────────────────────────────────────────

const AudioVariantsTabContent = memo(function AudioVariantsTabContent({
  variants,
  selectedIndex,
  batchPending,
  onSelect,
}: AudioVariantsTabContentProps) {
  const { t } = useTranslation('canvas')
  const audioElementsRef = useRef<Map<number, HTMLAudioElement>>(new Map())
  const activePlaybackIndexRef = useRef<number | null>(null)
  const sortedVariants = sortAudioVariantsNewestFirst(variants)
  const hasPending = batchPending > 0

  const handleStartPlayback = useCallback((index: number, element: HTMLAudioElement) => {
    const currentIndex = activePlaybackIndexRef.current
    const currentElement =
      currentIndex !== null ? audioElementsRef.current.get(currentIndex) ?? null : null

    if (currentElement && currentElement !== element) {
      currentElement.pause()
    }

    audioElementsRef.current.set(index, element)
    activePlaybackIndexRef.current = index
  }, [])

  const handlePlaybackStopped = useCallback((index: number) => {
    if (activePlaybackIndexRef.current === index) {
      activePlaybackIndexRef.current = null
    }
  }, [])

  if (sortedVariants.length === 0 && !hasPending) {
    return (
      <div
        className="h-full flex items-center justify-center"
        data-testid="audio-variants-empty"
      >
        <span className="text-xs font-medium text-text-muted">{t('audioVariantsTabContent.noAudioVariantsYet')}</span>
      </div>
    )
  }

  return (
    <div
      className="h-full overflow-y-auto nowheel px-2 py-2 space-y-2"
      data-testid="audio-variants-tab"
    >
      {hasPending &&
        Array.from({ length: batchPending }, (_, i) => <PendingRow key={`pending-${i}`} index={i} />)}

      {sortedVariants.map((variant) => (
        <VariantRow
          key={variant.index}
          variant={variant}
          isSelected={variant.index === selectedIndex}
          onSelect={() => onSelect(variant.index)}
          onStartPlayback={handleStartPlayback}
          onPlaybackStopped={handlePlaybackStopped}
        />
      ))}
    </div>
  )
})

AudioVariantsTabContent.displayName = 'AudioVariantsTabContent'

export { AudioVariantsTabContent }
